import React from "react";
import {
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  Chip,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { DashboardStats } from "../../../../types";

interface OverviewTabProps {
  stats: DashboardStats;
}

const OverviewTab: React.FC<OverviewTabProps> = ({ stats }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
        {t("dashboard.quickActions")}
      </Typography>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, minmax(0, 1fr))",
            md: "repeat(3, minmax(0, 1fr))",
            lg: "repeat(4, minmax(0, 1fr))",
          },
          gap: 2,
        }}
      >
        <Button
          fullWidth
          variant="outlined"
          onClick={() => navigate("/admin/products")}
          sx={{
            py: 2,
            borderColor: "rgba(0,212,255,0.5)",
            color: "#00d4ff",
            "&:hover": {
              borderColor: "#00d4ff",
              bgcolor: "rgba(0,212,255,0.1)",
            },
          }}
        >
          {t("dashboard.manageProducts")}
        </Button>
        <Button
          fullWidth
          variant="outlined"
          onClick={() => navigate("/admin/orders")}
          sx={{
            py: 2,
            borderColor: "rgba(255,0,128,0.5)",
            color: "#ff0080",
            "&:hover": {
              borderColor: "#ff0080",
              bgcolor: "rgba(255,0,128,0.1)",
            },
          }}
        >
          {t("dashboard.viewOrders")}
        </Button>
        <Button
          fullWidth
          variant="outlined"
          onClick={() => navigate("/admin/reports")}
          sx={{
            py: 2,
            borderColor: "rgba(0,255,136,0.5)",
            color: "#00ff88",
            "&:hover": {
              borderColor: "#00ff88",
              bgcolor: "rgba(0,255,136,0.1)",
            },
          }}
        >
          {t("dashboard.generateReports")}
        </Button>
        <Button
          fullWidth
          variant="outlined"
          onClick={() => navigate("/admin/cms")}
          sx={{
            py: 2,
            borderColor: "rgba(255,0,128,0.5)",
            color: "#ff0080",
            background:
              "linear-gradient(135deg, rgba(255,0,128,0.1), rgba(0,212,255,0.1))",
            "&:hover": {
              borderColor: "#ff0080",
              background:
                "linear-gradient(135deg, rgba(255,0,128,0.2), rgba(0,212,255,0.2))",
            },
          }}
        >
          {t("admin.contentManagementButton")}
        </Button>
        <Button
          fullWidth
          variant="outlined"
          onClick={() => navigate("/admin/users")}
          sx={{
            py: 2,
            borderColor: "rgba(0,0,0,0.2)",
            color: "text.primary",
            "&:hover": {
              borderColor: "#00d4ff",
              bgcolor: "rgba(0,212,255,0.08)",
            },
          }}
        >
          {t("admin.userManagement", {
            defaultValue: "User Management",
          })}
        </Button>
        <Button
          fullWidth
          variant="outlined"
          onClick={() => navigate("/admin/reviews")}
          sx={{
            py: 2,
            borderColor: "rgba(0,212,255,0.5)",
            color: "#00d4ff",
            "&:hover": {
              borderColor: "#00d4ff",
              bgcolor: "rgba(0,212,255,0.1)",
            },
          }}
        >
          {t("admin.sellerReviewsTitle", { defaultValue: "Seller Reviews" })}
        </Button>
      </Box>

      <Typography variant="h5" gutterBottom sx={{ mt: 4, mb: 3 }}>
        {t("dashboard.commissionSummary")}
      </Typography>
      <Card
        sx={{
          bgcolor: "rgba(255,0,128,0.05)",
          borderRadius: "8px",
          border: "1px solid rgba(255,0,128,0.2)",
        }}
      >
        <CardContent>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                md: "repeat(2, 1fr)",
              },
              gap: 3,
              alignItems: "center",
            }}
          >
            <Box>
              <Typography variant="h6" gutterBottom>
                {t("dashboard.totalCommissionEarned")}
              </Typography>
              <Typography
                variant="h3"
                sx={{ color: "#ff0080", fontWeight: 700 }}
              >
                €{stats.commission_earned.toFixed(2)}
              </Typography>
            </Box>
            <Box>
              <Typography
                variant="body2"
                color="text.secondary"
                gutterBottom
              >
                {t("dashboard.commissionRate")}
              </Typography>
              <Chip
                label={t("dashboard.fifteenPercentOnSales")}
                sx={{
                  bgcolor: "rgba(255,0,128,0.2)",
                  color: "#ff0080",
                  fontWeight: 600,
                }}
              />
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default OverviewTab;